// The immersive vertical-snap feed (APP-2182) — the full-viewport TikTok-style
// player a viewer scrolls, driven by the normalized `/feed/:portalUuid` contract.
// Its mechanics are adopted from the Riffland Portals prototype (the `rail`
// variant of `PortalPhone.dc.html`), reimplemented as a dependency-free browser
// module.
//
// Deliberately imperative: slides are built once, and only the ±1 near-window
// mounts a `<video>` — everyone else shows a poster. One video plays at a time;
// nothing plays sound until the viewer unmutes inside the open feed.

// The client-facing feed contract (APP-2175) is the shared wire contract both the
// feed proxy and this bundle speak. It is imported `import type`, so esbuild erases
// it and the bundle still reaches into no server code.
import type { Cta, FeedItem, FeedPage } from "../shared/feed-contract.js";
import { injectStyles } from "./dom.js";
import { track, EVENTS } from "./track.js";

export interface MountFeedOptions {
  /** The feed endpoint to page, e.g. `/feed/portal-christchurch`. */
  feedUrl: string;
  /**
   * The {@link FeedItem} id to open on, so the rail can dive in exactly where the
   * viewer tapped. Omitted (or not found) → the feed opens at the top. Pages are
   * loaded until the target clip appears, then the feed snaps to it.
   */
  startId?: string;
  /** Invoked when the viewer taps the close (X); defaults to tearing down. */
  onClose?: () => void;
}

/** Handle returned by {@link mountFeed}; `destroy` tears the feed down cleanly. */
export interface FeedController {
  destroy: () => void;
}

// Only the active clip and its immediate neighbors mount a real <video>.
const NEAR_WINDOW = 1;
// Start fetching the next page once the active clip is this close to the end.
const PREFETCH_THRESHOLD = 3;
// Hold the spinner back this long so it only appears for genuinely slow loads.
const SPINNER_DELAY_MS = 350;
// A clip must stay active this long before it counts as an impression (APP-2187),
// so flick-scrolls and loop-wraps past a slide don't inflate the count.
const IMPRESSION_DWELL_MS = 1000;
// Watch milestones reported as `crowdriff_vv_video_progress` (percent param).
const PROGRESS_MILESTONES = [25, 50, 75, 100] as const;
// GA4 caps a parameter value at 100 chars; keep `cta_url` under it (APP-2187).
const PARAM_VALUE_MAX = 100;

const STYLE_ID = "crvf-styles";

// The page-wide audio preference (APP-2185): the viewer's enabled/disabled-audio
// intent for the fullscreen feed. Module-level so it is shared by every feed
// mounted from this bundle (one preference across multiple galleries on a page)
// and survives a feed close/reopen within the page visit; in-memory only, so it
// resets to muted on a fresh page load. See CONTEXT.md "Audio preference".
let audioEnabled = false;

const ICON = {
  close: `<svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2.3" stroke-linecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>`,
  muted: `<svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H3v6h3l5 4z"/><path d="m17 9 4 6m0-6-4 6"/></svg>`,
  unmuted: `<svg viewBox="0 0 24 24" fill="none" stroke="#fff" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M11 5 6 9H3v6h3l5 4z"/><path d="M15.5 8.5a5 5 0 0 1 0 7M18.5 5.5a9 9 0 0 1 0 13"/></svg>`,
  play: `<svg viewBox="0 0 24 24" fill="#fff"><path d="M7 4l13 8-13 8z"/></svg>`,
  arrow: `<svg viewBox="0 0 24 18" fill="none" stroke="#037CC2" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"><path d="M2 9h19m-6-6 6 6-6 6"/></svg>`,
};

const CSS = `
.crvf-root{position:absolute;inset:0;background:#000;overflow:hidden;font-family:system-ui,-apple-system,Segoe UI,Roboto,sans-serif}
.crvf-stage{position:absolute;inset:0;overflow:hidden;background:#0a0a0b}
@media (min-width:641px){.crvf-stage{right:auto;left:50%;transform:translateX(-50%);width:auto;height:100%;aspect-ratio:9/16;max-width:100%}}
.crvf-scroller{position:absolute;inset:0;overflow-y:scroll;overflow-x:hidden;scroll-snap-type:y mandatory;scrollbar-width:none;-ms-overflow-style:none;overscroll-behavior:contain}
.crvf-scroller::-webkit-scrollbar{display:none}
.crvf-slide{position:relative;height:100%;width:100%;scroll-snap-align:start;scroll-snap-stop:always;background:#0a0a0b;overflow:hidden}
.crvf-poster,.crvf-video{position:absolute;inset:0;width:100%;height:100%;object-fit:cover}
.crvf-poster{z-index:0}
.crvf-video{z-index:1}
.crvf-tap{position:absolute;inset:0;z-index:2;cursor:pointer;touch-action:pan-y}
.crvf-scrim{position:absolute;inset:0;z-index:3;pointer-events:none;background:linear-gradient(180deg,rgba(8,7,6,.55) 0%,rgba(8,7,6,0) 22%,rgba(8,7,6,0) 52%,rgba(8,7,6,.82) 100%)}
.crvf-overlay{position:absolute;left:20px;right:78px;z-index:4;pointer-events:none}
.crvf-creator{font-size:14px;font-weight:600;color:#fff;letter-spacing:.1px}
.crvf-caption{font-size:14px;line-height:1.45;color:rgba(255,255,255,.9);margin-top:6px;overflow-wrap:anywhere;position:relative;pointer-events:none;display:flex;flex-direction:column;align-items:flex-start}
.crvf-caption-text{display:-webkit-box;-webkit-box-orient:vertical;-webkit-line-clamp:3;overflow:hidden}
.crvf-caption-toggle{display:none;align-items:center;align-self:flex-end;margin-top:8px;padding:0;border:0;background:none;color:rgba(255,255,255,.58);font:inherit;font-size:13px;font-weight:600;letter-spacing:.2px;cursor:pointer;pointer-events:auto;transition:color .15s ease}
.crvf-caption-toggle:hover{color:#fff}
.crvf-caption-toggle::after{content:"";flex:none;width:6px;height:6px;margin-left:6px;border-right:1.6px solid currentColor;border-bottom:1.6px solid currentColor;transform:translateY(-1px) rotate(45deg);transition:transform .2s ease}
.crvf-caption.crvf-clamped .crvf-caption-toggle{display:inline-flex}
.crvf-caption.crvf-expanded{pointer-events:auto;background:rgba(0,0,0,.6);border-radius:12px;padding:10px 12px;overscroll-behavior:contain}
.crvf-caption.crvf-expanded .crvf-caption-text{display:block;-webkit-line-clamp:none;min-height:0;overflow-y:auto;overscroll-behavior:contain;touch-action:pan-y}
.crvf-caption.crvf-expanded .crvf-caption-toggle::after{transform:translateY(1px) rotate(225deg)}
.crvf-caption-catch{position:absolute;inset:0;z-index:3;pointer-events:none}
.crvf-caption-catch.crvf-active{pointer-events:auto}
.crvf-cta{position:absolute;left:20px;right:20px;bottom:40px;z-index:5;display:flex;align-items:center;justify-content:center;gap:9px;height:50px;border-radius:999px;background:#fff;color:#171412;font-size:15.5px;font-weight:600;text-decoration:none;box-shadow:0 6px 22px rgba(0,0,0,.3)}
.crvf-cta:hover{background:#efe8dd}
.crvf-cta svg{width:16px;height:12px;flex:none}
.crvf-progress{position:absolute;top:0;left:0;right:0;height:3px;background:rgba(255,255,255,.14);z-index:20;pointer-events:none}
.crvf-progress-bar{height:100%;width:0;background:#fff}
.crvf-chrome{position:absolute;top:calc(env(safe-area-inset-top,0px) + 16px);right:14px;display:flex;gap:10px;z-index:20}
.crvf-btn{width:38px;height:38px;border-radius:999px;border:none;padding:0;background:rgba(12,10,9,.42);-webkit-backdrop-filter:blur(8px);backdrop-filter:blur(8px);display:flex;align-items:center;justify-content:center;cursor:pointer}
.crvf-btn:hover{background:rgba(12,10,9,.7)}
.crvf-btn svg{width:17px;height:17px}
.crvf-glyph{position:absolute;top:50%;left:50%;transform:translate(-50%,-50%);width:74px;height:74px;border-radius:999px;background:rgba(14,12,10,.42);-webkit-backdrop-filter:blur(3px);backdrop-filter:blur(3px);display:flex;align-items:center;justify-content:center;z-index:15;pointer-events:none}
.crvf-glyph[hidden]{display:none}
.crvf-glyph svg{width:30px;height:30px}
.crvf-spinner{position:absolute;top:50%;left:50%;width:44px;height:44px;margin:-22px 0 0 -22px;border-radius:999px;border:3px solid rgba(255,255,255,.25);border-top-color:#fff;z-index:14;pointer-events:none;animation:crvf-spin .8s linear infinite}
@keyframes crvf-spin{to{transform:rotate(360deg)}}
.crvf-message{position:absolute;inset:0;display:flex;align-items:center;justify-content:center;padding:0 32px;text-align:center;color:rgba(255,255,255,.72);font-size:15px;line-height:1.5;z-index:16}
`;

function posterImg(url: string): HTMLImageElement {
  const img = document.createElement("img");
  img.className = "crvf-poster";
  img.src = url;
  img.alt = "";
  return img;
}

// Ceiling for an expanded caption's scrollable text: it grows to at most this
// fraction of the visible slide height, then scrolls internally, so the full
// caption is always reachable without ever burying the video (APP-2186).
const CAPTION_EXPANDED_MAX = 0.55;

// The per-slide caption handles (APP-2186), present only when the item has one.
// Expand/collapse is transient per-slide state: `expanded` resets to collapsed
// whenever the slide leaves view. `expandable` is set once the caption is
// measured and found to overflow its 3-line collapsed cap.
interface SlideCaption {
  root: HTMLDivElement; // .crvf-caption — the collapsed cap / expanded panel
  text: HTMLDivElement; // .crvf-caption-text — the line-clamped / scrollable body
  toggle: HTMLButtonElement; // the "more"/"less" control
  catcher: HTMLDivElement; // full-slide tap catcher, live only while expanded
  measured: boolean;
  measurePending: boolean; // a retry-after-layout is already scheduled
  expandable: boolean;
  expanded: boolean;
}

interface Slide {
  el: HTMLDivElement;
  i: number;
  video: HTMLVideoElement | null;
  /** The media URL currently set on `video`, so a preview→full swap is detectable. */
  srcUrl: string | null;
  caption: SlideCaption | null;
}

class Feed implements FeedController {
  private readonly root: HTMLDivElement;
  private readonly stage: HTMLDivElement;
  private readonly scroller: HTMLDivElement;
  private readonly progressBar: HTMLDivElement;
  private readonly muteBtn: HTMLButtonElement;
  private readonly glyph: HTMLDivElement;
  private readonly spinner: HTMLDivElement;

  private readonly items: FeedItem[] = [];
  private readonly slides: Slide[] = [];
  private sentinel: HTMLDivElement | null = null;

  private cta: Cta | undefined;
  /** Clips the viewer has committed to full quality (tapped or unmuted). */
  private readonly engaged = new Set<number>();
  private active = 0;
  private paused = false;

  private cursor: string | null = null;
  private loading = false;
  private complete = false;
  private started = false;
  private destroyed = false;

  private scrollRaf = 0;
  private progressRaf = 0;
  private wrapTimer = 0;
  private resizeTimer = 0;
  private spinnerTimer = 0;
  private impressionTimer = 0;

  // Analytics dedupe, per feed session (APP-2187): a clip's impression fires at
  // most once, and each (clip, milestone) progress step at most once, so a loop
  // wrap or replay of an already-seen clip never re-counts.
  private readonly impressed = new Set<string>();
  private readonly progressed = new Set<string>();

  constructor(
    private readonly container: HTMLElement,
    private readonly options: MountFeedOptions,
  ) {
    injectStyles(container, STYLE_ID, CSS);
    container.textContent = "";

    this.root = this.div("crvf-root");
    this.stage = this.div("crvf-stage");
    this.scroller = this.div("crvf-scroller");
    this.stage.appendChild(this.scroller);

    const progress = this.div("crvf-progress");
    this.progressBar = this.div("crvf-progress-bar");
    progress.appendChild(this.progressBar);
    this.stage.appendChild(progress);

    const chrome = this.div("crvf-chrome");
    this.muteBtn = this.button(ICON.muted, () => this.toggleMute());
    this.syncMuteButton(); // a reopen with audio already on opens showing unmuted
    chrome.appendChild(this.muteBtn);
    chrome.appendChild(this.button(ICON.close, () => this.close()));
    this.stage.appendChild(chrome);

    this.glyph = this.div("crvf-glyph");
    this.glyph.innerHTML = ICON.play;
    this.glyph.hidden = true;
    this.stage.appendChild(this.glyph);

    this.spinner = this.div("crvf-spinner");
    this.spinner.hidden = true;
    this.stage.appendChild(this.spinner);

    this.root.appendChild(this.stage);
    container.appendChild(this.root);

    // The desktop letterbox (the black area beside the 9:16 stage) is `root`
    // itself; a click that lands on it — not on any stage descendant — dismisses
    // the feed, mirroring the close (X) button and Escape.
    this.root.addEventListener("click", this.onBackdropClick);
    this.scroller.addEventListener("scroll", this.onScroll, { passive: true });
    window.addEventListener("resize", this.onResize);
    window.addEventListener("keydown", this.onKeyDown);

    void this.loadNextPage();
  }

  // --- small DOM helpers -----------------------------------------------------

  private div(className: string): HTMLDivElement {
    const el = document.createElement("div");
    el.className = className;
    return el;
  }

  private button(icon: string, onClick: () => void): HTMLButtonElement {
    const b = document.createElement("button");
    b.className = "crvf-btn";
    b.type = "button";
    b.innerHTML = icon;
    b.addEventListener("click", onClick);
    return b;
  }

  // --- data loading ----------------------------------------------------------

  private async loadNextPage(): Promise<void> {
    if (this.loading || this.complete || this.destroyed) return;
    this.loading = true;
    try {
      const url = this.cursor
        ? `${this.options.feedUrl}?cursor=${encodeURIComponent(this.cursor)}`
        : this.options.feedUrl;
      const res = await fetch(url);
      if (!res.ok) throw new Error(`feed responded ${res.status}`);
      const page = (await res.json()) as FeedPage;
      if (this.destroyed) return;

      if (this.items.length === 0) this.cta = page.portal.cta; // read once, first page

      for (const item of page.items) this.addSlide(item);
      this.cursor = page.cursor;
      if (this.cursor === null) {
        this.complete = true;
        this.addSentinel();
      }

      if (!this.started) this.tryStart();
      else {
        this.mountWindow();
        this.sync();
      }
    } catch {
      if (this.items.length === 0) this.showMessage("This feed couldn’t be loaded.");
      // A page needed to locate `startId` failed — open at the top with what loaded
      // rather than retrying the failing page forever.
      else this.tryStart(true);
    } finally {
      this.loading = false;
      // Keep paging until we can open on the requested clip (or the feed ends and
      // we fall back to the top). Once started, scroll/prefetch drives paging.
      if (!this.started && !this.complete && this.items.length > 0) void this.loadNextPage();
    }
  }

  // Open the feed once we have the clip to open on: the `startId` target if given
  // (waiting for later pages while it might still arrive), else the top.
  private tryStart(force = false): void {
    if (this.started || this.items.length === 0) return;
    let start = 0;
    if (this.options.startId) {
      const idx = this.items.findIndex((it) => it.id === this.options.startId);
      if (idx >= 0) start = idx;
      else if (!this.complete && !force) return; // target may be on a later page — wait for it
    }
    this.started = true;
    this.active = start;
    this.mountWindow();
    requestAnimationFrame(() => {
      this.scroller.scrollTop = this.active * this.scroller.clientHeight;
      this.sync();
    });
    this.maybePrefetch();
    this.scheduleImpression();
  }

  // Fetch the next page when the active clip nears the end of what's loaded.
  private maybePrefetch(): void {
    if (!this.complete && !this.loading && this.active >= this.items.length - PREFETCH_THRESHOLD) {
      void this.loadNextPage();
    }
  }

  private addSlide(item: FeedItem): void {
    const i = this.items.length;
    this.items.push(item);

    const el = this.div("crvf-slide");
    el.appendChild(posterImg(item.poster.url));

    const tap = this.div("crvf-tap");
    tap.addEventListener("click", () => this.togglePause());
    el.appendChild(tap);

    el.appendChild(this.div("crvf-scrim"));

    const overlay = this.div("crvf-overlay");
    overlay.style.bottom = this.cta ? "112px" : "40px";
    const creator = item.author?.screen_name;
    if (creator) {
      const c = this.div("crvf-creator");
      c.textContent = `@${creator}`;
      overlay.appendChild(c);
    }
    const caption = item.caption ? this.buildCaption(item.caption, overlay, el) : null;
    el.appendChild(overlay);

    if (this.cta) {
      const cta = this.cta;
      const link = document.createElement("a");
      link.className = "crvf-cta";
      link.href = cta.url;
      link.target = "_blank";
      link.rel = "noopener noreferrer";
      link.textContent = cta.label;
      link.insertAdjacentHTML("beforeend", ICON.arrow);
      link.addEventListener("click", () => {
        track(EVENTS.ctaClick, {
          cta_label: cta.label,
          cta_url: cta.url.slice(0, PARAM_VALUE_MAX),
          item_id: item.id,
        });
      });
      el.appendChild(link);
    }

    // Slides go before the loop sentinel if it exists; it always stays last.
    this.scroller.insertBefore(el, this.sentinel);
    this.slides.push({ el, i, video: null, srcUrl: null, caption });
  }

  // --- captions (APP-2186) ---------------------------------------------------

  // Build the caption's DOM: a body clamped to 3 lines in the overlay, plus a
  // full-slide tap catcher appended to the slide (inert until the caption is
  // expanded). Overflow (and thus the "more" affordance) is only decided later,
  // in `measureCaption`, once the slide has real layout.
  private buildCaption(text: string, overlay: HTMLDivElement, slideEl: HTMLDivElement): SlideCaption {
    const root = this.div("crvf-caption");
    const body = this.div("crvf-caption-text");
    body.textContent = text;
    const toggle = document.createElement("button");
    toggle.className = "crvf-caption-toggle";
    toggle.type = "button";
    toggle.textContent = "more";
    toggle.setAttribute("aria-expanded", "false");
    root.appendChild(body);
    root.appendChild(toggle);
    overlay.appendChild(root);

    const catcher = this.div("crvf-caption-catch");
    slideEl.appendChild(catcher);

    const c: SlideCaption = {
      root,
      text: body,
      toggle,
      catcher,
      measured: false,
      measurePending: false,
      expandable: false,
      expanded: false,
    };
    // The toggle flips state; the catcher (live only while expanded) collapses on
    // a tap outside the panel and swallows it, so dismissing text never also
    // toggles play/pause (that lives on the lower `.crvf-tap` layer).
    toggle.addEventListener("click", () => this.toggleCaption(c));
    catcher.addEventListener("click", () => this.collapseCaption(c));
    return c;
  }

  // Decide, lazily, whether the collapsed caption overflows its 3-line cap and so
  // needs the "more" affordance + fade. If the slide isn't laid out yet, retry
  // after the next frame so the opening slide gets its affordance without waiting
  // for a scroll. `measured` is cleared on resize, where the wrap can change.
  private measureCaption(c: SlideCaption): void {
    if (c.measured || this.destroyed) return;
    if (c.text.clientHeight === 0) {
      if (c.measurePending) return;
      c.measurePending = true;
      requestAnimationFrame(() => {
        c.measurePending = false;
        this.measureCaption(c);
      });
      return;
    }
    c.measured = true;
    c.expandable = c.text.scrollHeight - c.text.clientHeight > 1;
    c.root.classList.toggle("crvf-clamped", c.expandable);
  }

  private toggleCaption(c: SlideCaption): void {
    if (c.expanded) this.collapseCaption(c);
    else this.expandCaption(c);
  }

  private expandCaption(c: SlideCaption): void {
    if (!c.expandable || c.expanded) return;
    c.expanded = true;
    // Cap the whole panel at ~55% of the visible slide; longer text scrolls
    // within it rather than climbing further up over the video.
    c.root.style.maxHeight = `${Math.round(CAPTION_EXPANDED_MAX * this.slideHeight())}px`;
    c.root.classList.add("crvf-expanded");
    c.catcher.classList.add("crvf-active");
    c.toggle.textContent = "less";
    c.toggle.setAttribute("aria-expanded", "true");
  }

  private collapseCaption(c: SlideCaption): void {
    if (!c.expanded) return;
    c.expanded = false;
    c.root.classList.remove("crvf-expanded");
    c.catcher.classList.remove("crvf-active");
    c.root.style.maxHeight = "";
    c.text.scrollTop = 0;
    c.toggle.textContent = "more";
    c.toggle.setAttribute("aria-expanded", "false");
  }

  // Reset every caption to collapsed — a slide leaving view always re-enters at
  // the clean default.
  private collapseAllCaptions(): void {
    for (const slide of this.slides) if (slide.caption) this.collapseCaption(slide.caption);
  }

  // A resize can rewrap a caption (notably the desktop 9:16 column ↔ full-bleed
  // mobile switch), so the 3-line overflow verdict has to be recomputed; mark
  // every caption for re-measurement and let the next `mountWindow` redo the ones
  // in view.
  private remeasureCaptions(): void {
    for (const slide of this.slides) {
      const c = slide.caption;
      if (!c) continue;
      c.measured = false;
      c.expandable = false;
      c.root.classList.remove("crvf-clamped");
    }
  }

  private slideHeight(): number {
    return this.scroller.clientHeight || this.stage.clientHeight || 0;
  }

  // A clone of the first clip's poster placed after the last real slide, giving
  // the viewer somewhere to scroll into; once settled there we jump silently back
  // to the top, so the end of the feed loops seamlessly.
  private addSentinel(): void {
    const first = this.items[0];
    if (!first) return;
    const el = this.div("crvf-slide");
    el.appendChild(posterImg(first.poster.url));
    this.scroller.appendChild(el);
    this.sentinel = el;
  }

  // --- the ±1 video near-window ---------------------------------------------

  private mountWindow(): void {
    for (const slide of this.slides) {
      if (Math.abs(slide.i - this.active) <= NEAR_WINDOW) {
        // While audio is enabled the near-window plays full (audio-bearing) assets:
        // the active clip so its sound is real now, the ±1 neighbors so sound is
        // instant on the next clip instead of arriving after a preview→full swap.
        // (Audio off → neighbors preview as before.)
        if (audioEnabled) this.engage(slide.i);
        this.ensureVideo(slide);
        if (slide.caption) this.measureCaption(slide.caption);
      } else this.unmountVideo(slide);
    }
  }

  private srcFor(i: number): string {
    const item = this.items[i];
    if (!item) return "";
    // Autoplay the lightweight preview clip when present; the full asset is used
    // once the viewer engages the clip (tap or unmute) — see `engage`.
    const engaged = this.engaged.has(i);
    if (!engaged && item.playback.preview) return item.playback.preview.url;
    return item.playback.full.url;
  }

  private ensureVideo(slide: Slide): void {
    if (slide.video) {
      this.refreshSrc(slide);
      return;
    }
    const v = document.createElement("video");
    v.className = "crvf-video";
    v.muted = true; // required for autoplay; the gate is enforced in `sync`
    v.defaultMuted = true;
    v.playsInline = true;
    v.setAttribute("playsinline", "");
    v.preload = "auto";
    const src = this.srcFor(slide.i);
    v.src = src;
    slide.srcUrl = src;

    const onReady = (): void => {
      if (slide.i === this.active) this.sync();
    };
    v.addEventListener("canplay", onReady);
    v.addEventListener("loadeddata", onReady);
    v.addEventListener("playing", onReady);
    v.addEventListener("ended", () => {
      // TikTok-style: when the active clip finishes, move to the next one rather
      // than replaying. Neighbors are paused, so only the active clip ends here.
      if (slide.i !== this.active) return;
      // Count only completion here — not every lower milestone — so a clip that
      // ends before the rAF tick sampled 25/50/75 doesn't emit them in a burst.
      const item = this.items[this.active];
      if (item) this.emitProgress(item, 100);
      this.advance();
    });

    // Stacking is by z-index (poster 0, video 1, tap 2, …), so DOM order is free.
    slide.el.appendChild(v);
    slide.video = v;
  }

  private unmountVideo(slide: Slide): void {
    const v = slide.video;
    if (!v) return;
    v.pause();
    v.removeAttribute("src");
    v.load();
    v.remove();
    slide.video = null;
    slide.srcUrl = null;
  }

  private refreshSrc(slide: Slide): void {
    const v = slide.video;
    if (!v) return;
    const desired = this.srcFor(slide.i);
    if (desired === slide.srcUrl) return;
    v.src = desired;
    slide.srcUrl = desired;
    v.load();
    if (slide.i === this.active && !this.paused) this.playActive(v);
  }

  // Commit a clip to its full-quality asset (it may autoplay only a preview).
  private engage(i: number): void {
    if (this.engaged.has(i)) return;
    const item = this.items[i];
    if (!item || !item.playback.preview) return; // no preview → already on full
    this.engaged.add(i);
    const slide = this.slides[i];
    if (slide) this.refreshSrc(slide);
  }

  // --- analytics (APP-2187) --------------------------------------------------

  // Arm the impression timer for whatever clip is active now: if it is still the
  // active slide ~1s later (and hasn't already been counted), it dwelled long
  // enough to count. Called on every active-slide change; the pending timer is
  // dropped first so a quick scroll-through never fires.
  private scheduleImpression(): void {
    clearTimeout(this.impressionTimer);
    const item = this.items[this.active];
    if (!item || this.impressed.has(item.id)) return;
    const id = item.id;
    this.impressionTimer = window.setTimeout(() => {
      const cur = this.items[this.active];
      if (this.destroyed || !cur || cur.id !== id || this.impressed.has(id)) return;
      this.impressed.add(id);
      track(EVENTS.videoImpression, { ...this.clipParams(cur), non_interaction: true });
    }, IMPRESSION_DWELL_MS);
  }

  // The `item_id` + `source` pair that identifies a clip on every clip-scoped
  // event — the funnel's per-asset grain (no portalUuid, no new identifiers).
  private clipParams(item: FeedItem): { item_id: string; source: string | undefined } {
    return { item_id: item.id, source: item.source };
  }

  // Emit `video_progress` for each watch milestone the active clip has now
  // reached but not yet reported this session (dedupe survives loop/replay).
  private recordProgress(pct: number): void {
    const item = this.items[this.active];
    if (!item) return;
    for (const milestone of PROGRESS_MILESTONES) {
      if (pct >= milestone) this.emitProgress(item, milestone);
    }
  }

  private emitProgress(item: FeedItem, milestone: number): void {
    const key = `${item.id}:${milestone}`;
    if (this.progressed.has(key)) return;
    this.progressed.add(key);
    track(EVENTS.videoProgress, {
      ...this.clipParams(item),
      percent: milestone,
      non_interaction: true,
    });
  }

  // --- playback state machine ------------------------------------------------

  // Exactly one clip plays at a time. The active clip plays (unless paused);
  // audio is allowed only for the active, unpaused clip once the viewer unmutes.
  // Every other mounted clip is paused and rewound to the start.
  private sync(): void {
    for (const slide of this.slides) {
      const v = slide.video;
      if (!v) continue;
      const isActive = slide.i === this.active;
      const wantAudio = isActive && audioEnabled && !this.paused;
      v.muted = !wantAudio;
      v.volume = wantAudio ? 1 : 0;
      if (isActive && !this.paused) {
        this.playActive(v);
      } else {
        if (!v.paused) v.pause();
        if (!isActive && v.currentTime > 0.05) {
          try {
            v.currentTime = 0;
          } catch {
            /* not seekable yet */
          }
        }
      }
    }
    this.glyph.hidden = !this.paused;
    this.updateSpinner();

    // Drive the progress bar off a per-frame sample of the active clip instead of
    // the video's coarse, irregular `timeupdate` — that's what made it look jerky.
    if (this.slides[this.active]?.video && !this.paused) this.startProgress();
    else this.stopProgress();
  }

  // Play the active clip, degrading the audio preference to muted if the browser
  // refuses unmuted playback (`play()` rejects with a NotAllowedError). Muting the
  // clip, flipping the button icon, and flipping the preference together keep the
  // three from ever diverging into a lying control; the viewer re-enables with a
  // tap, which carries a fresh user gesture and succeeds. A rejection for any other
  // reason (e.g. a load interrupted by the next scroll) is ignored, as before.
  private playActive(v: HTMLVideoElement): void {
    void v.play().catch((err: unknown) => {
      if (audioEnabled && (err as { name?: string } | null)?.name === "NotAllowedError") {
        this.handleBlockedAudio();
      }
    });
  }

  private handleBlockedAudio(): void {
    if (!audioEnabled) return;
    audioEnabled = false;
    this.syncMuteButton();
    this.sync(); // re-mute the clip honestly; the now-muted play() succeeds
  }

  private syncMuteButton(): void {
    this.muteBtn.innerHTML = audioEnabled ? ICON.unmuted : ICON.muted;
  }

  // Advance the progress bar in lockstep with the display refresh (~60fps) while
  // the active clip plays. The loop runs off our own `paused` flag rather than the
  // video's transient `paused` state, so it survives the async `play()` handshake
  // and a buffering stall (the bar simply holds until `currentTime` moves again).
  private startProgress(): void {
    if (this.progressRaf) return;
    this.progressRaf = requestAnimationFrame(this.tickProgress);
  }

  private stopProgress(): void {
    if (!this.progressRaf) return;
    cancelAnimationFrame(this.progressRaf);
    this.progressRaf = 0;
  }

  private readonly tickProgress = (): void => {
    const v = this.slides[this.active]?.video;
    if (v && v.duration > 0) {
      const pct = Math.min(1, v.currentTime / v.duration) * 100;
      this.progressBar.style.width = `${pct}%`;
      this.recordProgress(pct);
    }
    this.progressRaf =
      this.paused || this.destroyed ? 0 : requestAnimationFrame(this.tickProgress);
  };

  private updateSpinner(): void {
    const v = this.slides[this.active]?.video;
    const loading = v != null && v.readyState < 3 && !this.paused;
    clearTimeout(this.spinnerTimer);
    if (loading) {
      // Only reveal the spinner if the clip is still not ready after a beat, so a
      // fast load (or a preview→full swap) never flashes it.
      this.spinnerTimer = window.setTimeout(() => {
        this.spinner.hidden = false;
      }, SPINNER_DELAY_MS);
    } else {
      this.spinner.hidden = true;
    }
  }

  // Auto-advance when the active clip finishes playing (TikTok-style). Scrolling
  // the next slide into view reuses the normal scroll → setActive machinery, so
  // the snap transition matches a manual swipe.
  private advance(): void {
    const next = this.active + 1;
    if (next < this.items.length) {
      const h = this.scroller.clientHeight || 1;
      this.scroller.scrollTo({ top: next * h, behavior: "smooth" });
      return;
    }
    if (this.complete) {
      // End of the feed: wrap silently to the top, mirroring the scroll wrap.
      this.scroller.scrollTop = 0;
      this.setActive(0);
      return;
    }
    // The next page is still loading; replay the current clip until it arrives.
    const v = this.slides[this.active]?.video;
    if (!v) return;
    try {
      v.currentTime = 0;
    } catch {
      /* not seekable yet */
    }
    this.playActive(v);
  }

  private setActive(n: number): void {
    if (n === this.active) return;
    this.collapseAllCaptions(); // the outgoing slide leaves view

    this.active = n;
    this.paused = false;
    this.progressBar.style.width = "0%";
    this.mountWindow();
    this.sync();
    this.maybePrefetch();
    this.scheduleImpression();
  }

  // --- interactions ----------------------------------------------------------

  private togglePause(): void {
    this.paused = !this.paused;
    // Commit the clip to its full asset only when resuming, so a pause tap keeps
    // the current frame instead of restarting on a preview→full swap.
    if (!this.paused) this.engage(this.active);
    this.sync();
  }

  private toggleMute(): void {
    audioEnabled = !audioEnabled;
    // Real audio lives on the full asset, so enabling upgrades the near-window (±1
    // pre-loaded so the next clip's sound is instant). Disabling needs no reload.
    if (audioEnabled) {
      this.mountWindow();
      // The unmute is the funnel's intent tier; audio-disable is deliberately untracked.
      const item = this.items[this.active];
      if (item) track(EVENTS.audioEnable, this.clipParams(item));
    }
    this.syncMuteButton();
    this.sync();
  }

  private close(): void {
    if (this.options.onClose) this.options.onClose();
    else this.destroy();
  }

  // A click on the black letterbox (the event target is `root`, never a stage
  // descendant) dismisses the feed, mirroring the close (X) button and Escape.
  private readonly onBackdropClick = (e: MouseEvent): void => {
    if (e.target === this.root) this.close();
  };

  // Escape dismisses the full-viewport feed, mirroring the close (X) button.
  private readonly onKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      e.preventDefault();
      this.close();
    }
  };

  // --- scroll / resize -------------------------------------------------------

  private readonly onScroll = (): void => {
    if (this.scrollRaf) return; // coalesce bursts of scroll events into one frame
    this.scrollRaf = requestAnimationFrame(() => {
      this.scrollRaf = 0;
      const h = this.scroller.clientHeight || 1;
      const n = Math.round(this.scroller.scrollTop / h);
      if (this.complete && n >= this.items.length) {
        this.scheduleWrap();
        return;
      }
      if (n >= 0 && n < this.items.length) this.setActive(n);
    });
  };

  private scheduleWrap(): void {
    clearTimeout(this.wrapTimer);
    this.wrapTimer = window.setTimeout(() => {
      const h = this.scroller.clientHeight || 1;
      if (Math.round(this.scroller.scrollTop / h) >= this.items.length) {
        this.scroller.scrollTop = 0; // identical poster → the wrap is invisible
        this.setActive(0);
      }
    }, 140);
  }

  private readonly onResize = (): void => {
    clearTimeout(this.resizeTimer);
    this.resizeTimer = window.setTimeout(() => {
      // A resize changes both the 55% ceiling and the wrap, so drop any expansion
      // and re-measure overflow against the new dimensions.
      this.collapseAllCaptions();
      this.remeasureCaptions();
      this.scroller.scrollTop = this.active * this.scroller.clientHeight;
      this.mountWindow();
    }, 150);
  };

  private showMessage(text: string): void {
    const msg = this.div("crvf-message");
    msg.textContent = text;
    this.stage.appendChild(msg);
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.root.removeEventListener("click", this.onBackdropClick);
    this.scroller.removeEventListener("scroll", this.onScroll);
    window.removeEventListener("resize", this.onResize);
    window.removeEventListener("keydown", this.onKeyDown);
    cancelAnimationFrame(this.scrollRaf);
    this.stopProgress();
    clearTimeout(this.wrapTimer);
    clearTimeout(this.resizeTimer);
    clearTimeout(this.spinnerTimer);
    clearTimeout(this.impressionTimer);
    for (const slide of this.slides) this.unmountVideo(slide);
    this.root.remove();
  }
}

/**
 * Mount the immersive vertical-snap feed into `container` (which should establish
 * a positioned, full-viewport box — the feed fills it, letterboxing to a 9:16
 * column on desktop and going full-bleed on mobile). Pass `startId` to open on a
 * specific clip (the rail entry point); it opens at the top otherwise. Returns a
 * controller whose `destroy()` tears everything down.
 */
export function mountFeed(container: HTMLElement, options: MountFeedOptions): FeedController {
  return new Feed(container, options);
}
